/**
 * The Todae storefront's routes — and, on todae.net, the whole site.
 *
 * This module is the shop half of the two-platform split (src/config/platform.ts).
 * It is imported two ways, and that is the point:
 *
 *   • the planner build imports `shopChildren` and hangs it under /todae, so
 *     todaidesign.net keeps working exactly as before;
 *   • the shop build aliases `@/routes` straight here (see vite.config.ts), so
 *     todae.net gets `routes` below and never parses the planner tree at all —
 *     no designer, no calculators, no admin, no 26k SEO pages.
 *
 * The child paths are written once and shared, because a child path is relative
 * to whichever parent carries it: mounted under `path: 'todae'` the product page
 * is /todae/p/:slug, mounted under a pathless layout route it is /p/:slug. Only
 * `getStaticPaths` has to know the difference — it returns ABSOLUTE paths — so
 * those build from SHOP_PREFIX rather than a hardcoded 'todae/'.
 */
import React from 'react';
import type { RouteObject } from 'react-router-dom';
import RootLayout from './layouts/RootLayout';
import RootErrorBoundary from './components/RootErrorBoundary';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SHOP_PREFIX } from '@/config/platform';
import { SHOP_CATEGORIES } from '@/config/todaeShop';

const ShopLayout = React.lazy(() => import('./components/shop/chrome/ShopLayout'));
const StoreHome = React.lazy(() => import('./pages/todae/StoreHome'));
const CategoryListing = React.lazy(() => import('./pages/todae/CategoryListing'));
const ProductPage = React.lazy(() => import('./pages/todae/ProductPage'));
const SearchPage = React.lazy(() => import('./pages/todae/SearchPage'));
const CartPage = React.lazy(() => import('./pages/todae/CartPage'));
const CartImportPage = React.lazy(() => import('./pages/todae/CartImportPage'));
const CheckoutPage = React.lazy(() => import('./pages/todae/CheckoutPage'));
const CheckoutSuccessPage = React.lazy(() => import('./pages/todae/CheckoutSuccessPage'));
const AccountOrdersPage = React.lazy(() => import('./pages/todae/AccountOrdersPage'));
const AccountOrderDetailPage = React.lazy(() => import('./pages/todae/AccountOrderDetailPage'));
const AccountPage = React.lazy(() => import('./pages/todae/AccountPage'));
const AccountQuotesPage = React.lazy(() => import('./pages/todae/AccountQuotesPage'));
const ShopAuthPage = React.lazy(() => import('./pages/todae/AuthPage'));
const QuotePage = React.lazy(() => import('./pages/todae/QuotePage'));
const RmaPage = React.lazy(() => import('./pages/todae/RmaPage'));
const OrderTrackPage = React.lazy(() => import('./pages/todae/OrderTrackPage'));
const PrivacyPOPIA = React.lazy(() => import('./pages/todae/legal/PrivacyPOPIA'));
const TermsOfSale = React.lazy(() => import('./pages/todae/legal/TermsOfSale'));
const ReturnsPolicy = React.lazy(() => import('./pages/todae/legal/ReturnsPolicy'));
const ShippingPolicy = React.lazy(() => import('./pages/todae/legal/ShippingPolicy'));
const PaiaManual = React.lazy(() => import('./pages/todae/legal/PaiaManual'));
const UsPrivacy = React.lazy(() => import('./pages/todae/legal/UsPrivacy'));
const Auth = React.lazy(() => import('./pages/Auth'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Todae PDP static paths — BOTH Tier-A full PDPs and Tier-B noindex stubs are
// prerendered from the same slug registry (ProductPage decides which to render
// per the product's `tier`; the noindex is baked into the served HTML, §10).
// Sourced from the persisted slug map so URLs never churn on catalog re-import.
// Lazy import keeps the 3,349-entry map out of the CSR bundle — getStaticPaths
// only runs during SSG. (Draft SKUs resolve to null in ProductPage; a status-
// filtered source can replace this once the build exposes one — see decisions.)
const getTodaeProductPaths = () =>
  import('@/data/todai/slugs.json').then((m) =>
    Object.values(m.default as Record<string, string>).map((slug) => `${SHOP_PREFIX}p/${slug}`),
  );

/**
 * The storefront itself. The home, category and product pages plus the legal
 * set are SSG'd; the transactional children (cart, checkout, account, rma,
 * quote, order tracking and the CSR search) are excluded in vite.config.ts
 * because they read auth-dependent data. EN-only at launch — no /:lang subtree.
 */
export const shopChildren: RouteObject[] = [
  { index: true, element: <StoreHome /> },
  {
    path: 'c/:category',
    element: <CategoryListing />,
    getStaticPaths: () => Object.keys(SHOP_CATEGORIES).map((c) => `${SHOP_PREFIX}c/${c}`),
  },
  {
    path: 'p/:slug',
    element: <ProductPage />,
    getStaticPaths: getTodaeProductPaths,
  },
  { path: 'search', element: <SearchPage /> },
  { path: 'cart', element: <CartPage /> },
  { path: 'cart/import', element: <CartImportPage /> },
  { path: 'checkout', element: <CheckoutPage /> },
  { path: 'checkout/success', element: <CheckoutSuccessPage /> },
  { path: 'auth', element: <ShopAuthPage /> },
  { path: 'account', element: <AccountPage /> },
  { path: 'account/quotes', element: <AccountQuotesPage /> },
  { path: 'account/orders', element: <AccountOrdersPage /> },
  { path: 'account/orders/:orderNumber', element: <AccountOrderDetailPage /> },
  { path: 'quote', element: <QuotePage /> },
  { path: 'rma', element: <RmaPage /> },
  { path: 'order/track', element: <OrderTrackPage /> },
  // Legal set (launch blockers, SSG, ZA-first — §9). These are Todae (Pty) Ltd's
  // own terms, not TodAidesign's: the buyer contracts with the shop.
  { path: 'legal/privacy', element: <PrivacyPOPIA /> },
  { path: 'legal/terms', element: <TermsOfSale /> },
  { path: 'legal/returns', element: <ReturnsPolicy /> },
  { path: 'legal/shipping', element: <ShippingPolicy /> },
  { path: 'legal/paia', element: <PaiaManual /> },
  { path: 'legal/us-privacy', element: <UsPrivacy /> },
];

/** ShopLayout applies the `.todae-shop` scope, renders the shop header/footer
 *  and provides the cart context. */
export const shopBranch = (
  <ErrorBoundary name="todae-shop">
    <ShopLayout />
  </ErrorBoundary>
);

/**
 * todae.net's entire route table. RootLayout still wraps everything — it owns
 * auth, i18n, currency and theme, which the storefront needs — but the shop
 * layout sits directly beneath it as a PATHLESS route, so the storefront home
 * is `/` rather than `/todae`.
 */
export const routes: RouteObject[] = [
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { element: shopBranch, children: shopChildren },
      { path: '404', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];
